import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3150';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3150/initLU + logicalUnitCode fusionné côté shell. */
export interface U3150InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3150/search. */
export interface U3150TaskSearchRow {
  id: string;
  taskCode?: string;
  active?: boolean;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3150TaskTranslation {
  id?: string;
  languageCode: string;
  taskDescription: string;
  version?: number;
}

/** Détail GET /U3150/:id (result). */
export interface U3150Task {
  id: string;
  taskCode: string;
  active: boolean;
  translations: U3150TaskTranslation[];
  version: number;
}

export type U3150TranslationFormRow = U3150TaskTranslation;

export type U3150TaskFormRow = U3150Task;

export interface U3150TaskCreateBody {
  taskCode: string;
  active: boolean;
  translations: Array<{
    languageCode: string;
    taskDescription: string;
  }>;
}

export interface U3150TaskUpdateBody {
  id: string;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        taskDescription: string;
      }
    | {
        id: string;
        languageCode: string;
        taskDescription: string;
        version: number;
      }
  >;
}

export interface U3150TaskDeleteBody {
  id: string;
  version: number;
}

export const U3150_ERROR_CODE_DUPLICATE = 'BU315001';
export const U3150_ERROR_CODE_IN_USE = 'BU315002';

export function createEmptyU3150FormRow(): U3150TaskFormRow {
  return {
    id: '',
    taskCode: '',
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', taskDescription: '' },
      { languageCode: 'en', taskDescription: '' },
    ],
  };
}

export function normalizeU3150FromApi(row: U3150Task): U3150Task {
  return {
    ...row,
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3150TranslationDescription(
  translations: U3150TaskTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.taskDescription?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (descriptions FR/EN) à partir du détail fiche — évite la perte d'affichage liste après GET/save. */
export function u3150FormRowToSearchRow(
  row: U3150Task | U3150TaskFormRow,
  existing?: U3150TaskSearchRow | null,
): U3150TaskSearchRow {
  const searchRow: U3150TaskSearchRow = {
    id: row.id,
    taskCode: row.taskCode,
    active: row.active !== false,
    taskDescriptionFr: u3150TranslationDescription(row.translations, 'fr'),
    taskDescriptionEn: u3150TranslationDescription(row.translations, 'en'),
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3150FormatTaskListLabel(row: U3150TaskSearchRow, language: string): string {
  const code = row.taskCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.taskDescriptionFr : row.taskDescriptionEn)?.trim() ||
    row.taskDescriptionFr?.trim() ||
    row.taskDescriptionEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3150TaskFormRow): U3150TaskCreateBody {
  return {
    taskCode: data.taskCode.trim(),
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      taskDescription: tr.taskDescription.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3150TaskFormRow): U3150TaskUpdateBody {
  return {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const taskDescription = tr.taskDescription.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          taskDescription,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, taskDescription };
    }),
  };
}

export function buildDeleteBody(data: U3150TaskFormRow): U3150TaskDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3150TaskCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3150TaskUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3150TaskDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3150_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['taskCode'] = t('errTaskCodeDuplicate', { ns: nameSpace });
      break;
    case U3150_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
